import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "../lib/api";
import Button from "../components/ui/Button";
import ListingCard, { ListingCardSkeleton } from "../components/ListingCard";
import { useSeo } from "../lib/seo";

const LANGUAGE_NAMES = { en: "English", rw: "Ikinyarwanda" };

/**
 * A service or city page — "Hair salons in Kigali" — at /services/{category},
 * /services/{category}/{city} and /locations/{city}. These are the pages
 * search engines list, so everything on them comes from the shops that are
 * really there; the API has no page for a category or place without any.
 * With lang="rw" it is the Kinyarwanda version, under /rw.
 */
export default function ServiceLanding({ lang = "en" }) {
  const { category, city } = useParams();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let current = true;
    setPage(null);
    setMissing(false);

    api
      .get("/seo/page", { params: { category, city, lang } })
      .then(({ data }) => current && setPage(data))
      .catch(() => current && setMissing(true));

    return () => {
      current = false;
    };
  }, [category, city, lang]);

  // The page in its other language, for visitors and for search engines.
  const versions = page?.alternates.length > 1 ? page.alternates : [];

  useSeo(
    page
      ? {
          title: page.title,
          description: page.description,
          canonical: page.path,
          jsonLd: page.json_ld,
          lang: page.locale,
          alternates: versions.length
            ? [...versions.map((v) => ({ lang: v.locale, path: v.path })), { lang: "x-default", path: versions.find((v) => v.locale === "en").path }]
            : [],
        }
      : missing
      ? { title: "Nothing here yet | SHUWA", noindex: true }
      : null
  );

  if (missing) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center sm:px-6">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Nothing here yet</h1>
        <p className="mt-2 text-muted-foreground">
          No businesses on SHUWA match this page right now. Try a search instead.
        </p>
        <Button as={Link} to="/search" className="mt-6">
          Search businesses
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-slate-50">
      <div className="border-b border-border bg-white">
        <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
          {versions.length > 0 && (
            <div className="mb-3 flex items-center gap-1 text-sm sm:float-right sm:mb-0" aria-label={page.labels.language}>
              {versions.map((version) =>
                version.locale === page.locale ? (
                  <span key={version.locale} className="rounded-full bg-primary/10 px-3 py-1 font-medium text-primary">
                    {LANGUAGE_NAMES[version.locale]}
                  </span>
                ) : (
                  <Link
                    key={version.locale}
                    to={version.path}
                    hrefLang={version.locale}
                    lang={version.locale}
                    className="rounded-full px-3 py-1 font-medium text-slate-600 hover:bg-muted"
                  >
                    {LANGUAGE_NAMES[version.locale]}
                  </Link>
                )
              )}
            </div>
          )}
          {page && (
            <nav aria-label="Breadcrumb" className="mb-3 flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
              {page.breadcrumbs.map((crumb, i) => (
                <span key={crumb.path} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="h-3.5 w-3.5" />}
                  {i < page.breadcrumbs.length - 1 ? (
                    <Link to={crumb.path} className="hover:text-primary">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-slate-700">{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}
          <h1 className="font-montserrat text-2xl font-bold text-slate-900 sm:text-3xl">
            {page ? page.heading : <span className="inline-block h-8 w-64 animate-pulse rounded bg-muted" />}
          </h1>
          {page && (
            <>
              <p className="mt-3 max-w-3xl text-sm text-muted-foreground">{page.intro}</p>
              {page.local_line && <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{page.local_line}</p>}
            </>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="space-y-4">
          {page
            ? page.businesses.map((business) => <ListingCard key={business.id} business={business} labels={page.labels} />)
            : Array.from({ length: 4 }).map((_, i) => <ListingCardSkeleton key={i} />)}
        </div>

        {page && page.total > page.businesses.length && (
          <div className="mt-6 text-center">
            <Button
              as={Link}
              variant="outline"
              to={`/search?${new URLSearchParams({
                ...(page.category && { category: page.category.search }),
                ...(page.city && { city: page.city.name }),
              })}`}
            >
              {page.labels.see_all.replace(":total", page.total)}
            </Button>
          </div>
        )}

        {page?.related.length > 0 && (
          <div className="mt-10 border-t border-border pt-8">
            <h2 className="mb-3 font-montserrat text-lg font-semibold text-slate-900">{page.labels.more}</h2>
            <div className="flex flex-wrap gap-2">
              {page.related.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="rounded-full border border-border bg-white px-3.5 py-1.5 text-sm text-slate-700 transition-colors hover:border-primary hover:text-primary"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
